/* ==========================================================================
   OUTLYRS CLUB — outlyrs-gallery.js
   <outlyrs-gallery>: thumbnail switching, variant-driven media, fullscreen zoom.
   ========================================================================== */
(function () {
  "use strict";

  class OutlyrsGallery extends HTMLElement {
    connectedCallback() {
      this.slides = Array.prototype.slice.call(this.querySelectorAll("[data-media-id]"));
      this.thumbs = Array.prototype.slice.call(this.querySelectorAll("[data-thumb]"));
      this.viewer = this.querySelector("[data-viewer]");
      this.viewerFrame = this.querySelector("[data-viewer-frame]");
      // Current markup ships an empty frame and the <img> is created on first
      // open, which avoids an <img src=""> (browsers request the page itself).
      // Older markup shipped the <img> inline — adopt it if present so zoom
      // keeps working on any template still serving that version.
      this.viewerImg = this.querySelector("[data-viewer-img]");
      // An src="" makes the browser re-request the page itself and shows up in
      // audits as a broken image. Drop it until a real source is set on open.
      if (this.viewerImg && !this.viewerImg.getAttribute("src")) {
        this.viewerImg.removeAttribute("src");
      }
      this.viewerClose = this.querySelector("[data-viewer-close]");

      this.thumbs.forEach(function (thumb) {
        thumb.addEventListener("click", function () { this.activate(thumb.getAttribute("data-thumb")); }.bind(this));
      }, this);

      this.querySelectorAll("[data-zoom]").forEach(function (btn) {
        btn.addEventListener("click", function () { this.openViewer(btn.getAttribute("data-zoom-src"), btn.getAttribute("data-zoom-alt")); }.bind(this));
      }, this);

      this.main = this.querySelector(".gallery__main");
      this._wheelAcc = 0;
      this._wheelLock = false;
      if (this.main) this.main.addEventListener("wheel", this.onWheel.bind(this), { passive: false });

      this.prevBtn = this.querySelector("[data-gallery-prev]");
      this.nextBtn = this.querySelector("[data-gallery-next]");
      if (this.prevBtn) this.prevBtn.addEventListener("click", function () { this.step(-1); }.bind(this));
      if (this.nextBtn) this.nextBtn.addEventListener("click", function () { this.step(1); }.bind(this));

      if (this.viewerClose) this.viewerClose.addEventListener("click", this.closeViewer.bind(this));
      if (this.viewer) this.viewer.addEventListener("click", function (e) { if (e.target === this.viewer) this.closeViewer(); }.bind(this));

      document.addEventListener("variant:media", function (e) {
        if (e.detail && e.detail.mediaId) this.activate(String(e.detail.mediaId));
      }.bind(this));

      this._onKey = function (e) { if (e.key === "Escape") this.closeViewer(); }.bind(this);
    }

    activate(mediaId) {
      var isMobile = window.matchMedia("(max-width: 999px)").matches;
      this.slides.forEach(function (slide) {
        var match = slide.getAttribute("data-media-id") === mediaId;
        slide.classList.toggle("is-active", match);
        if (match && isMobile && typeof slide.scrollIntoView === "function") {
          slide.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
        }
      });
      this.thumbs.forEach(function (thumb) {
        thumb.classList.toggle("is-active", thumb.getAttribute("data-thumb") === mediaId);
      });
    }

    currentIndex() {
      for (var i = 0; i < this.slides.length; i++) {
        if (this.slides[i].classList.contains("is-active")) return i;
      }
      return 0;
    }

    step(dir) {
      if (this.slides.length < 2) return;
      var idx = this.currentIndex();
      var next = (idx + dir + this.slides.length) % this.slides.length;
      this.activate(this.slides[next].getAttribute("data-media-id"));
    }

    onWheel(e) {
      if (this.slides.length < 2) return;
      if (window.matchMedia("(max-width: 999px)").matches) return; // mobile uses swipe
      var idx = this.currentIndex();
      var atStart = idx <= 0;
      var atEnd = idx >= this.slides.length - 1;
      // At the ends, let the wheel scroll the page normally (never trap the user).
      if ((e.deltaY > 0 && atEnd) || (e.deltaY < 0 && atStart)) return;
      e.preventDefault();
      if (this._wheelLock) return;
      this._wheelAcc += e.deltaY;
      if (Math.abs(this._wheelAcc) > 20) {
        var dir = this._wheelAcc > 0 ? 1 : -1;
        this._wheelAcc = 0;
        var next = Math.min(this.slides.length - 1, Math.max(0, idx + dir));
        this.activate(this.slides[next].getAttribute("data-media-id"));
        this._wheelLock = true;
        var self = this;
        setTimeout(function () { self._wheelLock = false; }, 320);
      }
    }

    openViewer(src, alt) {
      // Only the viewer and a source are required: the frame is optional so a
      // missing one can never silently disable zoom.
      if (!this.viewer || !src) return;
      if (!this.viewerImg) {
        this.viewerImg = document.createElement("img");
        (this.viewerFrame || this.viewer).appendChild(this.viewerImg);
      }
      this.viewerImg.src = src;
      this.viewerImg.alt = alt || "";
      this.viewer.classList.add("is-open");
      document.body.classList.add("is-locked");
      document.addEventListener("keydown", this._onKey);
      if (this.viewerClose) this.viewerClose.focus();
    }

    closeViewer() {
      if (!this.viewer) return;
      this.viewer.classList.remove("is-open");
      document.body.classList.remove("is-locked");
      document.removeEventListener("keydown", this._onKey);
    }
  }

  if (!customElements.get("outlyrs-gallery")) customElements.define("outlyrs-gallery", OutlyrsGallery);
})();
